import { create } from "zustand";
import { demoUser, isDemoMode } from "@/lib/demo";
import { supabase } from "@/lib/supabase";
import { withTimeout } from "@/lib/async";
import type { User } from "@mira/types";

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasInitialized: boolean;
  setUser: (user: User | null) => void;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
  initialize: () => Promise<void>;
}

interface AuthSessionUser {
  id: string;
  email?: string | null;
  user_metadata?: Record<string, unknown>;
}

function mapProfileToUser(
  profile: Record<string, unknown> | null,
  sessionUser: AuthSessionUser,
): User | null {
  const metadata = sessionUser.user_metadata ?? {};
  const email = sessionUser.email ?? "";
  const fallbackFullName =
    metadata.full_name ?? metadata.name ?? metadata.display_name ?? null;
  const fallbackCompanyName =
    metadata.company_name ?? metadata.company ?? metadata.organization ?? null;

  if (!profile) {
    return {
      id: sessionUser.id,
      email,
      fullName: fallbackFullName ? String(fallbackFullName) : null,
      companyName: fallbackCompanyName ? String(fallbackCompanyName) : null,
      avatarUrl: metadata.avatar_url ? String(metadata.avatar_url) : null,
      createdAt: new Date().toISOString(),
    };
  }
  return {
    id: profile.id ? String(profile.id) : sessionUser.id,
    email,
    fullName: profile.full_name
      ? String(profile.full_name)
      : fallbackFullName
        ? String(fallbackFullName)
        : null,
    companyName: profile.company_name
      ? String(profile.company_name)
      : fallbackCompanyName
        ? String(fallbackCompanyName)
        : null,
    avatarUrl: profile.avatar_url
      ? String(profile.avatar_url)
      : metadata.avatar_url
        ? String(metadata.avatar_url)
        : null,
    createdAt: profile.created_at
      ? String(profile.created_at)
      : new Date().toISOString(),
  };
}

async function loadProfileUser(sessionUser: AuthSessionUser): Promise<User | null> {
  const { data: profile } = await withTimeout(
    supabase
      .from("profiles")
      .select("*")
      .eq("id", sessionUser.id)
      .maybeSingle(),
    8_000,
  );

  return mapProfileToUser(profile, sessionUser);
}

function scheduleProfileRetry(sessionUser: AuthSessionUser) {
  window.setTimeout(() => {
    const currentUser = useAuthStore.getState().user;
    if (!currentUser || currentUser.id !== sessionUser.id) return;
    if (currentUser.fullName || currentUser.companyName) return;
    void useAuthStore.getState().refreshProfile();
  }, 1_500);
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  hasInitialized: false,
  setUser: (user) => set({ user, isAuthenticated: !!user }),
  signOut: async () => {
    if (isDemoMode) {
      set({ user: demoUser, isAuthenticated: true });
      return;
    }

    await supabase.auth.signOut();
    set({ user: null, isAuthenticated: false });
  },
  refreshProfile: async () => {
    if (isDemoMode) {
      set({ user: demoUser, isAuthenticated: true, isLoading: false });
      return;
    }

    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.user) {
      set({ user: null, isAuthenticated: false, isLoading: false });
      return;
    }

    const user = await loadProfileUser(session.user).catch(() =>
      mapProfileToUser(null, session.user),
    );

    set({
      user,
      isAuthenticated: true,
      isLoading: false,
    });
  },
  initialize: async () => {
    const state = useAuthStore.getState();
    if (state.hasInitialized) return;
    set({ hasInitialized: true });

    if (isDemoMode) {
      set({ user: demoUser, isAuthenticated: true, isLoading: false });
      return;
    }

    try {
      const {
        data: { session },
      } = await withTimeout(supabase.auth.getSession(), 8_000);

      if (session?.user) {
        const user = await loadProfileUser(session.user);

        set({
          user,
          isAuthenticated: true,
          isLoading: false,
        });
        if (!user?.fullName && !user?.companyName) {
          scheduleProfileRetry(session.user);
        }
      } else {
        set({ isLoading: false });
      }
    } catch {
      const fallbackSession = await supabase.auth
        .getSession()
        .catch(() => ({ data: { session: null } }));
      const session = fallbackSession.data.session;
      set({
        user: session?.user
          ? mapProfileToUser(null, session.user)
          : null,
        isAuthenticated: !!session?.user,
        isLoading: false,
      });
      if (session?.user) {
        scheduleProfileRetry(session.user);
      }
    }

    supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        const user = await loadProfileUser(session.user).catch(() =>
          mapProfileToUser(null, session.user),
        );

        set({
          user,
          isAuthenticated: true,
        });
        if (!user?.fullName && !user?.companyName) {
          scheduleProfileRetry(session.user);
        }
      } else {
        set({ user: null, isAuthenticated: false });
      }
    });
  },
}));
